import React, { useState, useEffect } from 'react';
import { summaryAPI, skillsAPI, experienceAPI, projectsAPI, educationAPI, profilePictureAPI } from '../services/api';

const Portfolio = () => {
  const [summary, setSummary] = useState({ content: '' });
  const [skills, setSkills] = useState([]);
  const [experience, setExperience] = useState([]);
  const [projects, setProjects] = useState([]);
  const [education, setEducation] = useState([]);
  const [profilePicture, setProfilePicture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPortfolioData();
  }, []);

  const loadPortfolioData = async () => {
    try {
      setLoading(true);
      const [summaryRes, skillsRes, experienceRes, projectsRes, educationRes, profilePicRes] = await Promise.all([
        summaryAPI.get(),
        skillsAPI.getAll(),
        experienceAPI.getAll(),
        projectsAPI.getAll(),
        educationAPI.getAll(),
        profilePictureAPI.get().catch(() => ({ data: { file_path: null } })),
      ]);

      setSummary(summaryRes.data);
      setSkills(skillsRes.data);
      setExperience(experienceRes.data);
      setProjects(projectsRes.data);
      setEducation(educationRes.data);
      setProfilePicture(profilePicRes.data);
      setError(null);
    } catch (err) {
      console.error('Error loading portfolio data:', err);
      setError('Failed to load portfolio data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  const formatYearRange = (startYear, endYear) => {
    if (!endYear) return `${startYear}– Present`;
    return `${startYear}– ${endYear}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800 dark:from-slate-900 dark:to-slate-900 flex items-center justify-center">
        <div className="text-white dark:text-gray-200 text-xl">Loading portfolio...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800 dark:from-slate-900 dark:to-slate-900 flex items-center justify-center">
        <div className="text-red-300 dark:text-red-400 text-xl">{error}</div>
      </div>
    );
  }

  const getProfilePictureUrl = () => {
    if (!profilePicture || !profilePicture.file_path) return true;
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
    return `${baseUrl}${profilePicture.file_path}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-purple-800 dark:from-slate-900 dark:to-slate-900 py-8 px-4">
      <div className="max-w-6xl mx-auto bg-white dark:bg-slate-800 rounded-3xl shadow-2xl p-8 md:p-12">
        <header className="text-center pb-8 border-b-4 border-purple-600 dark:border-purple-500 mb-12 relative">
          {getProfilePictureUrl() && (
            <div className="flex justify-center mb-6">
              <img 
                src="/ved-photo.png"
                alt="Profile" 
                className="w-48 h-48 rounded-full object-cover border-4 border-purple-600 dark:border-purple-500 shadow-xl hover:scale-105 transition-transform duration-300"
              />
            </div>
          )}
          <h1 className="text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-purple-800 dark:from-purple-400 dark:to-purple-600 bg-clip-text text-transparent">
            VED VIKAS
          </h1>
          <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-gray-700 dark:text-gray-400 text-sm md:text-base">
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              +91-7764916491
            </span>
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              vedvikas477@gmail.com
            </span>
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              <a href="https://linkedin.com/in/ved-vikas-ab925278" target="_blank" rel="noopener noreferrer" className="hover:underline">linkedin.com/in/ved-vikas</a>
            </span>
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Bengaluru, India
            </span>
          </div>
        </header>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-6 pb-2 border-b-2 border-purple-600 dark:border-purple-500 flex items-center gap-3">
            <span className="w-1 h-8 bg-gradient-to-b from-purple-600 to-purple-800 dark:from-purple-400 dark:to-purple-600 rounded"></span>
            Professional Summary
          </h2>
          <div className="bg-gray-50 dark:bg-slate-700 p-6 rounded-xl leading-relaxed text-gray-700 dark:text-gray-300 text-lg border-l-4 border-purple-600 dark:border-purple-500">
            {summary.content || 'No summary available'}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-6 pb-2 border-b-2 border-purple-600 dark:border-purple-500 flex items-center gap-3">
            <span className="w-1 h-8 bg-gradient-to-b from-purple-600 to-purple-800 dark:from-purple-400 dark:to-purple-600 rounded"></span>
            Technical Skills
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {skills.map((skill) => (
              <div 
                key={skill.id} 
                className="bg-gradient-to-br from-purple-600 to-purple-800 dark:from-purple-500 dark:to-purple-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden"
              >
                <div className="bg-purple-800/50 dark:bg-purple-600/50 px-6 py-4 border-b border-white/20">
                  <h3 className="text-xl font-bold">{skill.category}</h3>
                </div>
                <ul className="space-y-2 p-6 pt-4">
                  {skill.skills.map((item, index) => (
                    <li key={index} className="py-2 border-b border-white/20 last:border-b-0">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-6 pb-2 border-b-2 border-purple-600 dark:border-purple-500 flex items-center gap-3">
            <span className="w-1 h-8 bg-gradient-to-b from-purple-600 to-purple-800 dark:from-purple-400 dark:to-purple-600 rounded"></span>
            Work Experience
          </h2>
          <div className="space-y-6">
            {experience.map((exp) => (
              <div 
                key={exp.id} 
                className="bg-gray-50 dark:bg-slate-700 p-6 rounded-xl border-l-4 border-purple-600 dark:border-purple-500 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4 gap-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">{exp.title}</h3>
                    <div className="text-purple-600 dark:text-purple-400 font-semibold">{exp.company}</div>
                    {exp.location && <div className="text-gray-600 dark:text-gray-400 mt-1">{exp.location}</div>}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400 text-sm md:text-base whitespace-nowrap">
                    {formatDate(exp.start_date)} - {exp.is_current ? 'Present' : formatDate(exp.end_date)}
                  </div>
                </div>
                {exp.description && exp.description.length > 0 && (
                  <ul className="space-y-2 mt-4">
                    {exp.description.map((item, index) => (
                      <li key={index} className="text-gray-700 dark:text-gray-300 pl-6 relative before:content-['▸'] before:absolute before:left-0 before:text-purple-600 dark:before:text-purple-400 before:font-bold">
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-6 pb-2 border-b-2 border-purple-600 dark:border-purple-500 flex items-center gap-3">
            <span className="w-1 h-8 bg-gradient-to-b from-purple-600 to-purple-800 dark:from-purple-400 dark:to-purple-600 rounded"></span>
            Projects
          </h2>
          <div className="space-y-6">
            {projects.map((project) => (
              <div 
                key={project.id} 
                className="bg-gray-50 dark:bg-slate-700 p-6 rounded-xl border-l-4 border-purple-600 dark:border-purple-500 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4 gap-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">{project.name}</h3>
                    {project.client && <div className="text-purple-600 dark:text-purple-400 font-semibold">Client: {project.client}</div>}
                    {project.url && (
                      <div className="mt-2">
                        <a 
                          href={project.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-blue-600 dark:text-blue-400 hover:underline font-medium inline-flex items-center gap-1"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          View Project
                        </a>
                      </div>
                    )}
                  </div>
                  {project.start_date && (
                    <div className="text-gray-600 dark:text-gray-400 text-sm md:text-base whitespace-nowrap">
                      {formatDate(project.start_date)} - {project.is_current ? 'Present' : formatDate(project.end_date)}
                    </div>
                  )}
                </div>
                {project.technologies && project.technologies.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {project.technologies.map((tech, index) => (
                      <span 
                        key={index} 
                        className="bg-purple-600 dark:bg-purple-500 text-white px-3 py-1 rounded-full text-sm font-medium"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                )}
                {project.description && project.description.length > 0 && (
                  <ul className="space-y-2 mt-4">
                    {project.description.map((item, index) => (
                      <li key={index} className="text-gray-700 dark:text-gray-300 pl-6 relative before:content-['▸'] before:absolute before:left-0 before:text-purple-600 dark:before:text-purple-400 before:font-bold">
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-6 pb-2 border-b-2 border-purple-600 dark:border-purple-500 flex items-center gap-3">
            <span className="w-1 h-8 bg-gradient-to-b from-purple-600 to-purple-800 dark:from-purple-400 dark:to-purple-600 rounded"></span>
            Education
          </h2>
          <div className="space-y-6">
            {education.map((edu) => (
              <div 
                key={edu.id} 
                className="bg-gray-50 dark:bg-slate-700 p-6 rounded-xl border-l-4 border-purple-600 dark:border-purple-500 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-4 gap-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2">{edu.degree}</h3>
                    <div className="text-gray-600 dark:text-gray-400 font-semibold">{edu.institution}</div>
                    {edu.location && <div className="text-gray-600 dark:text-gray-400 mt-1">{edu.location}</div>}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400 text-sm md:text-base whitespace-nowrap">
                    {formatYearRange(edu.start_year, edu.end_year)}
                  </div>
                </div>
                {edu.gpa && (
                  <div className="text-purple-600 dark:text-purple-400 font-semibold mt-2">
                    GPA: {edu.gpa}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default Portfolio;
